//@ts-nocheck
import { DatePicker } from 'antd';
import type { DatePickerProps } from 'antd';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { getLocalizedDatePickerFormat } from 'i18n/calendarLabels';
import { usesArabicIndicDigits } from 'i18n/arabicDigits';
import styled, { css } from 'styled-components';

const { RangePicker } = DatePicker;

const datePickerChromeCss = css`
  border-radius: 0;
  border: 1px solid transparent;
  padding: 6px 11px;
  min-width: 25%;
  height: 45px;
  &:hover,
  &:focus,
  &:active {
    border: 1px solid var(--color-primary, #41aaaf);
    box-shadow: none;
  }
  .ant-picker-suffix {
    background-image: url("data:image/svg+xml,%3Csvg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0_428_1184)'%3E%3Cpath d='M16.5004 1.6875H14.0625V0.5625C14.0625 0.251438 13.8111 0 13.5 0C13.1889 0 12.9375 0.251438 12.9375 0.5625V1.6875H9.5625V0.5625C9.5625 0.251438 9.3105 0 9 0C8.6895 0 8.4375 0.251438 8.4375 0.5625V1.6875H5.0625V0.5625C5.0625 0.251438 4.8105 0 4.5 0C4.1895 0 3.9375 0.251438 3.9375 0.5625V1.6875H1.50019C0.671625 1.6875 0 2.35856 0 3.18712V16.4998C0 17.3284 0.671625 18 1.50019 18H16.5004C17.3289 18 18 17.3284 18 16.4998V3.18712C18 2.35856 17.3289 1.6875 16.5004 1.6875ZM16.875 16.4998C16.875 16.7068 16.7068 16.875 16.5004 16.875H1.50019C1.29319 16.875 1.125 16.7068 1.125 16.4998V3.18712C1.125 2.98069 1.29319 2.8125 1.50019 2.8125H3.9375V3.9375C3.9375 4.24856 4.1895 4.5 4.5 4.5C4.8105 4.5 5.0625 4.24856 5.0625 3.9375V2.8125H8.4375V3.9375C8.4375 4.24856 8.6895 4.5 9 4.5C9.3105 4.5 9.5625 4.24856 9.5625 3.9375V2.8125H12.9375V3.9375C12.9375 4.24856 13.1889 4.5 13.5 4.5C13.8111 4.5 14.0625 4.24856 14.0625 3.9375V2.8125H16.5004C16.7068 2.8125 16.875 2.98069 16.875 3.18712V16.4998Z' fill='%2341AAAF'/%3E%3Cpath d='M6.1875 6.75H3.9375V8.4375H6.1875V6.75Z' fill='%2341AAAF'/%3E%3Cpath d='M6.1875 9.5625H3.9375V11.25H6.1875V9.5625Z' fill='%2341AAAF'/%3E%3Cpath d='M6.1875 12.375H3.9375V14.0625H6.1875V12.375Z' fill='%2341AAAF'/%3E%3Cpath d='M10.125 12.375H7.875V14.0625H10.125V12.375Z' fill='%2341AAAF'/%3E%3Cpath d='M10.125 9.5625H7.875V11.25H10.125V9.5625Z' fill='%2341AAAF'/%3E%3Cpath d='M10.125 6.75H7.875V8.4375H10.125V6.75Z' fill='%2341AAAF'/%3E%3Cpath d='M14.0625 12.375H11.8125V14.0625H14.0625V12.375Z' fill='%2341AAAF'/%3E%3Cpath d='M14.0625 9.5625H11.8125V11.25H14.0625V9.5625Z' fill='%2341AAAF'/%3E%3Cpath d='M14.0625 6.75H11.8125V8.4375H14.0625V6.75Z' fill='%2341AAAF'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_428_1184'%3E%3Crect width='18' height='18' fill='white'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E");
    width: 18px;
    height: 18px;
  }
  .anticon-calendar {
    color: transparent;
  }
  .ant-picker-input > input {
    color: var(--color-text-dark, #042a2a);
    font-weight: ${(props) => (props.hasValue ? '600' : '400')};
  }
`;

const StyledDatePicker = styled(DatePicker)`
  width: ${(props) => (props.width ? props.width : '90%')};
  ${datePickerChromeCss}
`;

interface FormRangePickerProps extends DatePickerProps {
  width?: string;
  hasValue?: boolean;
}

const StyledRangePicker = styled(RangePicker)<FormRangePickerProps>`
  width: ${(props) => (props.width ? props.width : '90%')};
  ${datePickerChromeCss}
`;

const useLocalizedPickerFormat = (format?: DatePickerProps['format']) => {
  const { i18n } = useTranslation();
  if (!usesArabicIndicDigits(i18n.language)) return format;
  if (typeof format === 'string') {
    return getLocalizedDatePickerFormat(format, i18n.language);
  }
  if (format == null) {
    return getLocalizedDatePickerFormat('DD/MM/YYYY', i18n.language);
  }
  return format;
};

export const FormDatePicker = React.forwardRef((props: any, ref) => {
  const format = useLocalizedPickerFormat(props.format);
  return <StyledDatePicker ref={ref} {...props} format={format} />;
});
FormDatePicker.displayName = 'FormDatePicker';

export const FormRangePicker = React.forwardRef((props: any, ref) => {
  const format = useLocalizedPickerFormat(props.format);
  return <StyledRangePicker ref={ref} {...props} format={format} />;
});
FormRangePicker.displayName = 'FormRangePicker';
